import { useCallback, useEffect, useState } from "react";

import { PaymentChip, RefundChip } from "@/components/chips";
import { Pager, Select } from "@/routes/orders";
import * as admin from "@/lib/api/admin";
import { ApiError } from "@/lib/api-client";
import type {
  ApiPayment,
  ApiRefund,
  ApiWebhookEvent,
  PaymentStatus,
  PaymentSummary,
  RefundStatus,
} from "@/lib/api/types";
import { cn, formatDateTime, formatMinor } from "@/lib/utils";

/**
 * Payments — the money view, in three tabs that answer three different
 * questions.
 *
 *   Payments  what has been collected, and which attempts died and why
 *   Refunds   what is on its way back, and which sends failed
 *   Webhooks  whether Cashfree is actually reaching us
 *
 * The third tab looks like plumbing and is the most operationally important of
 * the three. Every confirmation in this system arrives by webhook; when they
 * stop — a rotated secret, an expired tunnel, a firewall rule — nothing else
 * goes visibly wrong. Orders simply stop being confirmed, and the first signal
 * would otherwise be a customer asking why their paid order still says pending.
 * A count of rejected signatures on that tab is what turns a silent outage into
 * a visible one.
 */

type Tab = "payments" | "refunds" | "webhooks";

const TABS: { id: Tab; label: string }[] = [
  { id: "payments", label: "Payments" },
  { id: "refunds", label: "Refunds" },
  { id: "webhooks", label: "Webhooks" },
];

export function PaymentsRoute() {
  const [tab, setTab] = useState<Tab>("payments");
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void admin
      .fetchSummary(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setSummary(data);
      })
      .catch(() => {
        // A failed summary must not take the tables with it. The tiles simply
        // do not render; the screen is still fully usable without them.
      });

    return () => controller.abort();
  }, [reload]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-[20px] font-semibold text-ink">Payments</h1>
        <p className="mt-1 text-[13px] text-muted">
          Every Cashfree order, refund and webhook this system has seen.
        </p>
      </header>

      {summary ? <Summary summary={summary} /> : null}

      <div className="mb-4 flex gap-1 border-b border-line">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-[13px] font-semibold transition-colors",
              tab === item.id
                ? "border-ink text-ink"
                : "border-transparent text-muted hover:text-ink",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "payments" ? <PaymentsTable /> : null}
      {tab === "refunds" ? <RefundsTable onChanged={refresh} /> : null}
      {tab === "webhooks" ? <WebhooksTable /> : null}
    </div>
  );
}

/**
 * The tiles. `net` is the one that matters — collected minus refunded — and it
 * is computed from the tables rather than kept as a running total, because a
 * denormalised figure that drifts is worse than a query that takes 40ms.
 */
function Summary({ summary }: { summary: PaymentSummary }) {
  return (
    <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Tile
        label="Collected"
        value={formatMinor(summary.collected.minor)}
        note={`${summary.paidCount} paid`}
      />
      <Tile label="Refunded" value={formatMinor(summary.refunded.minor)} />
      <Tile label="Net" value={formatMinor(summary.net.minor)} strong />
      <Tile
        label="Awaiting / failed"
        value={`${summary.pendingCount} / ${summary.failedCount}`}
        {...(summary.invalidWebhooks24h > 0
          ? {
              /**
               * Surfaced here rather than buried in the webhooks tab because it
               * is the one number nobody would think to go and look at, and the
               * one whose failure is otherwise silent.
               */
              alert: `${summary.invalidWebhooks24h} webhook${
                summary.invalidWebhooks24h === 1 ? "" : "s"
              } rejected in 24h`,
            }
          : {})}
      />
    </div>
  );
}

/** Exported for the shipments screen's tiles, which are the same tiles. */
export function Tile({
  label,
  value,
  note,
  strong,
  alert,
}: {
  label: string;
  value: string;
  note?: string;
  strong?: boolean;
  alert?: string;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <p className="text-[11px] font-bold tracking-wide text-faint uppercase">{label}</p>
      <p
        className={cn(
          "mt-1 tabular-nums",
          strong ? "text-[22px] font-bold text-ink" : "text-[18px] font-semibold text-ink",
        )}
      >
        {value}
      </p>
      {note ? <p className="mt-0.5 text-[11.5px] text-faint">{note}</p> : null}
      {alert ? <p className="mt-1 text-[11.5px] font-semibold text-bad">{alert}</p> : null}
    </div>
  );
}

// ─── Payments ────────────────────────────────────────────────────────────────

const PAYMENT_STATUSES: PaymentStatus[] = [
  "CREATED",
  "PENDING",
  "PAID",
  "FAILED",
  "USER_DROPPED",
  "EXPIRED",
  "REFUNDED",
  "PARTIALLY_REFUNDED",
];

function PaymentsTable() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<PaymentStatus | "">("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<ApiPayment[] | null>(null);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await admin.fetchPayments(
          { page, status: status || undefined, search: search.trim() || undefined },
          controller.signal,
        );

        if (controller.signal.aborted) return;
        setRows(result.data);
        setPages(Math.max(1, result.meta.pages));
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load payments.");
        }
      }
    })();

    return () => controller.abort();
  }, [page, status, search]);

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Order number or Cashfree id…"
          className="w-[260px] rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
        />
        <Select
          value={status}
          onChange={(value) => {
            setStatus(value as PaymentStatus | "");
            setPage(1);
          }}
          options={[
            { value: "", label: "All statuses" },
            ...PAYMENT_STATUSES.map((s) => ({
              value: s,
              label: s.replace(/_/g, " ").toLowerCase(),
            })),
          ]}
        />
      </div>

      <Table
        error={error}
        rows={rows}
        empty="No payments match those filters."
        head={["Gateway order", "Customer", "Status", "Method", "Amount", "Created"]}
        render={(payment: ApiPayment) => (
          <tr key={payment.id} className="border-b border-line align-top last:border-0">
            <td className="px-4 py-3">
              <span className="block font-medium text-ink">{payment.gatewayOrderId}</span>
              {payment.cfPaymentId ? (
                <span className="block text-[11.5px] text-faint">cf {payment.cfPaymentId}</span>
              ) : null}
            </td>
            <td className="px-4 py-3 text-[12.5px] text-muted">
              {payment.order?.customerEmail ?? "—"}
            </td>
            <td className="px-4 py-3">
              <PaymentChip status={payment.status} />
              {/* Cashfree's own decline text. Useless to a shopper, first thing
                  support needs — which is why the two serializers differ. */}
              {payment.failureReason ? (
                <p className="mt-1 max-w-[260px] text-[11.5px] leading-relaxed text-bad">
                  {payment.failureReason}
                </p>
              ) : null}
            </td>
            <td className="px-4 py-3 text-[12px] text-muted uppercase">{payment.method ?? "—"}</td>
            <td className="px-4 py-3 text-right font-medium tabular-nums">
              {formatMinor(payment.amount.minor)}
              {payment.refunded.minor > 0 ? (
                <span className="block text-[11.5px] font-normal text-info">
                  −{formatMinor(payment.refunded.minor)}
                </span>
              ) : null}
            </td>
            <td className="px-4 py-3 text-[12px] text-muted">
              {formatDateTime(payment.createdAt)}
            </td>
          </tr>
        )}
      />

      <Pager page={page} pages={pages} onChange={setPage} />
    </>
  );
}

// ─── Refunds ─────────────────────────────────────────────────────────────────

const REFUND_STATUSES: RefundStatus[] = ["PENDING", "ONHOLD", "SUCCESS", "FAILED", "CANCELLED"];

function RefundsTable({ onChanged }: { onChanged: () => void }) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<RefundStatus | "">("");
  const [rows, setRows] = useState<ApiRefund[] | null>(null);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await admin.fetchRefunds(
          { page, status: status || undefined },
          controller.signal,
        );

        if (controller.signal.aborted) return;
        setRows(result.data);
        setPages(Math.max(1, result.meta.pages));
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load refunds.");
        }
      }
    })();

    return () => controller.abort();
  }, [page, status, reload]);

  /**
   * Retry is offered on anything not yet settled.
   *
   * Safe to press twice: the API returns early once the refund has a
   * `cfRefundId`, and Cashfree treats our `refundId` as an idempotency key. The
   * common case it exists for is a refund booked while Cashfree was
   * unreachable — the row is PENDING with a `failureReason`, and nothing else
   * will ever send it.
   */
  async function retry(refund: ApiRefund) {
    setBusy(refund.refundId);
    setError(null);

    try {
      await admin.retryRefund(refund.refundId);
      setReload((n) => n + 1);
      onChanged();
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "Could not retry that refund.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="mb-4">
        <Select
          value={status}
          onChange={(value) => {
            setStatus(value as RefundStatus | "");
            setPage(1);
          }}
          options={[
            { value: "", label: "All statuses" },
            ...REFUND_STATUSES.map((s) => ({ value: s, label: s.toLowerCase() })),
          ]}
        />
      </div>

      <Table
        error={error}
        rows={rows}
        empty="No refunds yet."
        head={["Refund", "Order", "Status", "Reason", "Amount", ""]}
        render={(refund: ApiRefund) => (
          <tr key={refund.id} className="border-b border-line align-top last:border-0">
            <td className="px-4 py-3">
              <span className="block font-medium text-ink">{refund.refundId}</span>
              {refund.arn ? (
                <span className="block text-[11.5px] text-faint">ARN {refund.arn}</span>
              ) : null}
            </td>
            <td className="px-4 py-3 text-[12.5px] text-muted">
              {refund.order?.orderNumber ?? "—"}
            </td>
            <td className="px-4 py-3">
              <RefundChip status={refund.status} />
              {refund.failureReason ? (
                <p className="mt-1 max-w-[240px] text-[11.5px] leading-relaxed text-bad">
                  {refund.failureReason}
                </p>
              ) : refund.statusDescription ? (
                <p className="mt-1 text-[11.5px] text-faint">{refund.statusDescription}</p>
              ) : null}
            </td>
            <td className="px-4 py-3 text-[12px] text-muted">
              {refund.reason.toLowerCase().replace(/_/g, " ")}
              <span className="block text-[11.5px] text-faint">
                {refund.actor === "system" ? "automatic" : "by an operator"}
              </span>
            </td>
            <td className="px-4 py-3 text-right font-medium tabular-nums">
              {formatMinor(refund.amount.minor)}
            </td>
            <td className="px-4 py-3 text-right">
              {refund.status === "SUCCESS" ? null : (
                <button
                  type="button"
                  disabled={busy === refund.refundId}
                  onClick={() => void retry(refund)}
                  className="rounded-lg border border-line px-2.5 py-1 text-[12px] font-semibold text-body hover:border-line-strong hover:text-ink disabled:opacity-50"
                >
                  {busy === refund.refundId ? "Sending…" : "Retry"}
                </button>
              )}
            </td>
          </tr>
        )}
      />

      <Pager page={page} pages={pages} onChange={setPage} />
    </>
  );
}

// ─── Webhooks ────────────────────────────────────────────────────────────────

function WebhooksTable() {
  const [page, setPage] = useState(1);
  const [invalidOnly, setInvalidOnly] = useState(false);
  const [rows, setRows] = useState<ApiWebhookEvent[] | null>(null);
  const [pages, setPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await admin.fetchWebhooks(
          { page, invalid: invalidOnly ? true : undefined },
          controller.signal,
        );

        if (controller.signal.aborted) return;
        setRows(result.data);
        setPages(Math.max(1, result.meta.pages));
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load webhooks.");
        }
      }
    })();

    return () => controller.abort();
  }, [page, invalidOnly]);

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-body">
          <input
            type="checkbox"
            checked={invalidOnly}
            onChange={(event) => {
              setInvalidOnly(event.target.checked);
              setPage(1);
            }}
          />
          Rejected signatures only
        </label>
      </div>

      <p className="mb-4 rounded-lg bg-info-tint px-4 py-3 text-[12.5px] leading-relaxed text-info">
        Every delivery is stored, verified or not. A run of rejected signatures means the secret
        rotated or somebody is probing the endpoint — and a long gap with no deliveries at all means
        Cashfree cannot reach us, which is the failure that otherwise shows up as orders quietly
        never being confirmed.
      </p>

      <Table
        error={error}
        rows={rows}
        empty="No webhooks received yet."
        head={["Received", "Event", "Signature", "Gateway order", "Handled"]}
        render={(event: ApiWebhookEvent) => (
          <WebhookRow
            key={event.id}
            event={event}
            expanded={open === event.id}
            onToggle={() => setOpen((current) => (current === event.id ? null : event.id))}
          />
        )}
      />

      <Pager page={page} pages={pages} onChange={setPage} />
    </>
  );
}

function WebhookRow({
  event,
  expanded,
  onToggle,
}: {
  event: ApiWebhookEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [payload, setPayload] = useState<unknown>(null);

  useEffect(() => {
    if (!expanded || payload !== null) return;

    const controller = new AbortController();

    void admin
      .fetchWebhook(event.id, controller.signal)
      .then((full) => {
        if (!controller.signal.aborted) setPayload(full.payload ?? {});
      })
      .catch(() => {
        if (!controller.signal.aborted) setPayload({ error: "Could not load the payload." });
      });

    return () => controller.abort();
  }, [expanded, event.id, payload]);

  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          "cursor-pointer border-b border-line last:border-0 hover:bg-canvas",
          expanded && "bg-canvas",
        )}
      >
        <td className="px-4 py-3 text-[12px] text-muted">{formatDateTime(event.receivedAt)}</td>
        <td className="px-4 py-3 text-[12.5px] font-medium text-ink">{event.type}</td>
        <td className="px-4 py-3">
          {event.signatureValid ? (
            <span className="text-[12px] text-good">Verified</span>
          ) : (
            <span className="text-[12px] font-semibold text-bad">Rejected</span>
          )}
        </td>
        <td className="px-4 py-3 text-[12px] text-muted">{event.gatewayOrderId ?? "—"}</td>
        <td className="px-4 py-3 text-[12px]">
          {event.error ? (
            <span className="text-bad">{event.error}</span>
          ) : event.processedAt ? (
            <span className="text-muted">Yes</span>
          ) : (
            /* Received and never handled: the handler threw. These are the rows
               to chase, and they are invisible without this column. */
            <span className="font-semibold text-warn">Not handled</span>
          )}
        </td>
      </tr>

      {expanded ? (
        <tr className="border-b border-line last:border-0">
          <td colSpan={5} className="bg-canvas px-4 py-3">
            <pre className="max-h-[320px] overflow-auto rounded-lg border border-line bg-surface p-3 text-[11.5px] leading-relaxed text-body">
              {payload === null ? "Loading…" : JSON.stringify(payload, null, 2)}
            </pre>
          </td>
        </tr>
      ) : null}
    </>
  );
}

// ─── Shared table shell ──────────────────────────────────────────────────────

/**
 * Exported rather than copied a third time.
 *
 * The shipments screen is the third table-with-filters in this console, and the
 * two that already existed had drifted in their loading and empty states before
 * anybody noticed. One shell, one set of states.
 */
export function Table<T>({
  rows,
  head,
  render,
  empty,
  error,
}: {
  rows: T[] | null;
  head: string[];
  render: (row: T) => React.ReactNode;
  empty: string;
  error: string | null;
}) {
  if (error) {
    return <p className="rounded-lg bg-bad-tint px-4 py-3 text-[13px] text-bad">{error}</p>;
  }

  if (rows === null) return <p className="text-[13.5px] text-muted">Loading…</p>;

  if (rows.length === 0) {
    return (
      <p className="rounded-xl border border-line bg-surface px-4 py-8 text-center text-[13.5px] text-muted">
        {empty}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-surface">
      <table className="w-full border-collapse text-left text-[13px]">
        <thead>
          <tr className="border-b border-line text-[11px] tracking-wide text-faint uppercase">
            {head.map((label, index) => (
              <th
                key={label || index}
                className={cn("px-4 py-3 font-semibold", label === "Amount" && "text-right")}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows.map(render)}</tbody>
      </table>
    </div>
  );
}
